import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import {
  buildTemuChildPath,
  buildTemuPath,
  buildTemuProductChildPath,
  isTemuRelativePathSupported,
  normalizeTemuShopTypeSlug,
} from '@/temu-platform/constants/temuRoutes';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

const ANALYSIS_PRODUCT_METRICS = [
  { key: 'listed', labelKey: 'platformHome.listed', tone: 'ok', path: '' },
  { key: 'pending', labelKey: 'platformHome.pendingList', tone: 'warn', path: '' },
  { key: 'soldOut', labelKey: 'platformHome.soldOut', tone: 'muted', path: '' },
  { key: 'offline', labelKey: 'platformHome.offline', tone: 'muted', path: '' },
  { key: 'drafts', labelKey: 'platformHome.draft', tone: 'info', path: 'drafts' },
];

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function extractTotal(res) {
  if (!res || typeof res !== 'object') return 0;
  if (Number.isFinite(Number(res.totalElements))) return Number(res.totalElements);
  if (Number.isFinite(Number(res.total))) return Number(res.total);
  const items = Array.isArray(res.items) ? res.items : (Array.isArray(res.content) ? res.content : []);
  return items.length;
}

function AnalysisBar({ label, value, max, tone = 'info', onClick, disabled }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <button
      type="button"
      className={`tpo-home-bar tpo-home-bar--${tone}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="tpo-home-bar__meta">
        <span className="tpo-home-bar__label">{label}</span>
        <span className="tpo-home-bar__value">{value}</span>
      </span>
      <span className="tpo-home-bar__track" aria-hidden>
        <span className="tpo-home-bar__fill" style={{ width: `${pct}%` }} />
      </span>
    </button>
  );
}

export default function TemuHomePage() {
  const { t } = useTranslation('workbench');
  const {
    scopedShops = [],
    concreteScopeId,
    allShopsScope,
    listReady,
    shopDimensionTabs,
  } = useTemuPageShopScope();
  const { shopType } = useParams();
  const slug = normalizeTemuShopTypeSlug(shopType) || 'semi';
  const isFull = slug === 'full';
  // 「自研专属接口」模块（核价/调价、营销活动）当前 ISV 应用无权限：
  // 不拉相关数据、不展示入口，避免无谓请求与必然失败的报错。唯一事实来源是 temuRoutes 的
  // TEMU_TRACK_GATED_PATHS —— 日后换自研应用时只要放开那里的 slugs，这里自动跟随。
  const showPricing = isTemuRelativePathSupported(slug, 'discuss');
  const showMarketing = isTemuRelativePathSupported(slug, 'marketing');
  const shopId = concreteScopeId || '';
  const scopedShopIds = useMemo(
    () => (scopedShops || []).map((s) => s?.id).filter((id) => id != null).map(String),
    [scopedShops],
  );
  const navigate = useNavigate();

  const [listingStats, setListingStats] = useState(null);
  const [reviewTotal, setReviewTotal] = useState(null);
  const [adjustTotal, setAdjustTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadSeqRef = useRef(0);

  const loadForShop = useCallback(async (id) => {
    const [statsRes, reviewsRes, adjustsRes] = await Promise.all([
      temuPlatformAPI.getProductListingStats(id).catch(() => null),
      showPricing
        ? temuPlatformAPI.listPriceReviews(id, { page: 0, size: 1 }).catch(() => null)
        : Promise.resolve(null),
      showPricing
        ? temuPlatformAPI.listPriceAdjusts(id, { page: 0, size: 1 }).catch(() => null)
        : Promise.resolve(null),
    ]);
    return {
      listingStats: statsRes || null,
      reviewTotal: showPricing ? extractTotal(reviewsRes) : null,
      adjustTotal: showPricing ? extractTotal(adjustsRes) : null,
    };
  }, [showPricing]);

  const loadLocalHome = useCallback(async ({ silent = false } = {}) => {
    if (!listReady) {
      setListingStats(null);
      setReviewTotal(null);
      setAdjustTotal(null);
      return;
    }
    const seq = ++loadSeqRef.current;
    if (!silent) setLoading(true);
    try {
      if (allShopsScope) {
        const rows = await Promise.all(scopedShopIds.map((id) => loadForShop(id)));
        if (seq !== loadSeqRef.current) return;
        const sumStats = {};
        let reviews = 0;
        let adjusts = 0;
        rows.forEach((row) => {
          reviews += num(row.reviewTotal);
          adjusts += num(row.adjustTotal);
          const stats = row.listingStats || {};
          Object.keys(stats).forEach((key) => {
            if (typeof stats[key] === 'number' || Number.isFinite(Number(stats[key]))) {
              sumStats[key] = num(sumStats[key]) + num(stats[key]);
            }
          });
        });
        setListingStats(Object.keys(sumStats).length ? sumStats : null);
        setReviewTotal(showPricing ? reviews : null);
        setAdjustTotal(showPricing ? adjusts : null);
      } else {
        const row = await loadForShop(shopId);
        if (seq !== loadSeqRef.current) return;
        setListingStats(row.listingStats);
        setReviewTotal(row.reviewTotal);
        setAdjustTotal(row.adjustTotal);
      }
    } catch (e) {
      if (!silent) showToast(e?.message || '\u52a0\u8f7d\u9996\u9875\u5931\u8d25', 'error');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [listReady, allShopsScope, scopedShopIds, shopId, loadForShop, showPricing]);

  useEffect(() => {
    loadLocalHome();
  }, [loadLocalHome]);

  const goProductMetric = (metric) => {
    if (metric.path === 'drafts') {
      navigate(buildTemuProductChildPath('drafts'));
      return;
    }
    navigate(buildTemuProductChildPath(''));
  };

  const priceTodoTotal = num(reviewTotal) + num(adjustTotal);
  const productTotal = useMemo(() => {
    // 卡片标题是「商品池（SPU）」：用按 SPU 去重的数量，别用 SKC 总数
    if (listingStats?.spuTotal != null) return num(listingStats.spuTotal);
    if (listingStats?.total != null) return num(listingStats.total);
    return ANALYSIS_PRODUCT_METRICS.reduce((sum, m) => sum + num(listingStats?.[m.key]), 0);
  }, [listingStats]);

  const productMax = useMemo(() => (
    Math.max(1, ...ANALYSIS_PRODUCT_METRICS.map((m) => num(listingStats?.[m.key])))
  ), [listingStats]);

  const formatMaybe = (value) => {
    if (loading && value == null) return '—';
    if (value == null) return '—';
    return value;
  };

  return (
    <ProductListCard tabs={shopDimensionTabs}>
      <div className="tpo-home">
        <section className="tpo-home-block tpo-home-section" aria-labelledby="tpo-home-analysis-title">
          <div className="tpo-home-section__head">
            <h2 id="tpo-home-analysis-title" className="tpo-home-section__title">{t('platformHome.analysis')}</h2>
            {allShopsScope && scopedShopIds.length > 0 ? (
              <p className="tpo-home-section__sub">{t('platformHome.scopeSummary', { count: scopedShopIds.length })}</p>
            ) : null}
          </div>

          <div className="tpo-home-kpi" role="list">
            {showPricing ? (
              <div className="tpo-home-kpi__card" role="listitem">
                <span className="tpo-home-kpi__label">{t('platformHome.priceTodos')}</span>
                <strong className={`tpo-home-kpi__value${priceTodoTotal > 0 ? ' is-alert' : ''}`}>
                  {loading && reviewTotal == null ? '—' : priceTodoTotal}
                </strong>
                <span className="tpo-home-kpi__hint">
                  {t('platformHome.temu.reviewAdjust', { review: formatMaybe(reviewTotal), adjust: formatMaybe(adjustTotal) })}
                </span>
              </div>
            ) : null}
            <div className="tpo-home-kpi__card" role="listitem">
              <span className="tpo-home-kpi__label">{t('platformHome.spuPool')}</span>
              <strong className="tpo-home-kpi__value">
                {loading && listingStats == null ? '—' : productTotal}
              </strong>
              <span className="tpo-home-kpi__hint">
                {t('platformHome.onOffShelf', { listed: num(listingStats?.listed), offline: num(listingStats?.offline) })}
              </span>
            </div>
            <div className="tpo-home-kpi__card" role="listitem">
              <span className="tpo-home-kpi__label">{t('platformHome.draftsBox')}</span>
              <strong className="tpo-home-kpi__value">{num(listingStats?.drafts)}</strong>
              <span className="tpo-home-kpi__hint">{t('platformHome.productDrafts')}</span>
            </div>
          </div>

          <div className="tpo-home-analysis-grid">
            <div className="tpo-home-panel">
              <h3 className="tpo-home-panel__title">{t('platformHome.structure')}</h3>
              <div className="tpo-home-bars">
                {ANALYSIS_PRODUCT_METRICS.map((metric) => (
                  <AnalysisBar
                    key={metric.key}
                    label={t(metric.labelKey)}
                    value={num(listingStats?.[metric.key])}
                    max={productMax}
                    tone={metric.tone}
                    disabled={!listReady}
                    onClick={() => goProductMetric(metric)}
                  />
                ))}
              </div>
            </div>
            <div className="tpo-home-panel">
              <h3 className="tpo-home-panel__title">{t('platformHome.shortcuts')}</h3>
              <ul className="tpo-home-breakdown">
                {showPricing ? (
                  <>
                    <li>
                      <button type="button" className="tpo-home-linkish" onClick={() => navigate(buildTemuChildPath('discuss', 'cost'))} disabled={!listReady}>
                        {t('platformHome.temu.reviewOrders')}
                      </button>
                      <strong className={num(reviewTotal) > 0 ? 'is-alert' : ''}>{formatMaybe(reviewTotal)}</strong>
                    </li>
                    <li>
                      <button type="button" className="tpo-home-linkish" onClick={() => navigate(buildTemuChildPath('discuss', 'rrp'))} disabled={!listReady}>
                        {t('platformHome.temu.adjustOrders')}
                      </button>
                      <strong className={num(adjustTotal) > 0 ? 'is-alert' : ''}>{formatMaybe(adjustTotal)}</strong>
                    </li>
                  </>
                ) : null}
                {showMarketing ? (
                  <li>
                    <button type="button" className="tpo-home-linkish" onClick={() => navigate(buildTemuPath('marketing'))} disabled={!listReady}>
                      {t('platformHome.campaigns')}
                    </button>
                    <strong>—</strong>
                  </li>
                ) : null}
                {isFull ? (
                  <li>
                    <button type="button" className="tpo-home-linkish" onClick={() => navigate(buildTemuChildPath('orders', 'stocking'))} disabled={!listReady}>
                      {t('platformHome.stockShip')}
                    </button>
                    <strong>—</strong>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        </section>

        {showPricing ? (
          <div className="tpo-home-block tpo-home-todos">
            <section className="tpo-home-section" aria-labelledby="tpo-home-price-title">
              <div className="tpo-home-section__head">
                <h2 id="tpo-home-price-title" className="tpo-home-section__title">{t('platformHome.priceTodos')}</h2>
              </div>
              <div className="tpo-todo-grid tpo-todo-grid--price" role="list">
                <button
                  type="button"
                  className={`tpo-todo-card${num(reviewTotal) > 0 ? ' tpo-todo-card--alert' : ''}`}
                  role="listitem"
                  onClick={() => navigate(buildTemuChildPath('discuss', 'cost'))}
                  disabled={!listReady}
                >
                  <div className="tpo-todo-card__head">
                    <h3 className="tpo-todo-card__title">{t('platformHome.temu.reviewTodo')}</h3>
                    <span className="tpo-todo-card__count">{formatMaybe(reviewTotal)}</span>
                  </div>
                  <p className="tpo-todo-card__desc">{t('platformHome.temu.reviewDesc')}</p>
                  <span className="tpo-todo-card__cta">{t('platformHome.temu.goReview')}</span>
                </button>
                <button
                  type="button"
                  className={`tpo-todo-card${num(adjustTotal) > 0 ? ' tpo-todo-card--alert' : ''}`}
                  role="listitem"
                  onClick={() => navigate(buildTemuChildPath('discuss', 'rrp'))}
                  disabled={!listReady}
                >
                  <div className="tpo-todo-card__head">
                    <h3 className="tpo-todo-card__title">{t('platformHome.temu.adjustTodo')}</h3>
                    <span className="tpo-todo-card__count">{formatMaybe(adjustTotal)}</span>
                  </div>
                  <p className="tpo-todo-card__desc">{t('platformHome.temu.adjustDesc')}</p>
                  <span className="tpo-todo-card__cta">{t('platformHome.temu.goAdjust')}</span>
                </button>
              </div>
            </section>
          </div>
        ) : null}

        {!listReady ? (
          <p className="tpo-empty-hint">{t('platformHome.needShop', { platform: 'TEMU' })}</p>
        ) : null}
      </div>
    </ProductListCard>
  );
}
